import { z } from 'zod';
import { ComponentValidator, ComponentPayloadValidator } from '../Component';
import { AbstractPort, Port } from '../fields/AbstractPort';
import { IsolationVoltage } from '../fields/IsolationVoltage';
import { Accuracy } from '../fields/Accuracy';
import { Time } from '../fields/Time';
import { ComponentDefinition } from '../../types/ComponentDefinition';

const inputPort = AbstractPort.extend({
    AC: Port.AC.default({}),
    DC: Port.DC.default({}),
    overvoltageCategory: z.number().optional(),
}).default({
    powerFlowDirection: 'input',
});

const outputPort = AbstractPort.extend({
    AC: Port.AC.default({}),
    DC: Port.DC.default({}),
    overvoltageCategory: z.number().optional(),
}).default({
    powerFlowDirection: 'output',
});

const electrical = z
    .object({
        ports: z.tuple([inputPort, outputPort]).default([{}, {}]),
        isolationVoltage: IsolationVoltage.validator,
        accuracy: Accuracy.validator,
        responseTime: Time.validator,
    })
    .default({});

const CurrentTransducerBase = z.object({
    type: z.literal('currentTransducer').default('currentTransducer'),
    electrical,
});

export const CurrentTransducerValidator = ComponentValidator.merge(CurrentTransducerBase);
export const CurrentTransducerPayloadValidator = ComponentPayloadValidator.merge(CurrentTransducerBase);

export const CurrentTransducerDefinition: ComponentDefinition = {
    type: 'currentTransducer',
    name: 'Current Transducer',
    plural: 'Current Transducers',
    indicator: 'CT',
    hasDynamicNumberOfPorts: false,
    interoperabilityDataModel: false,
    ports: {
        min: 2,
        max: 2,
        powerFlowDirections: ['input', 'output', 'bidirectional'],
        displayAsSinglePort: false,
        labels: ['Input Port', 'Output Port'],
    },
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: CurrentTransducerValidator,
        payload: CurrentTransducerPayloadValidator,
    },
};

export type CurrentTransducer = z.infer<typeof CurrentTransducerValidator>;
export type CurrentTransducerPayload = z.infer<typeof CurrentTransducerPayloadValidator>;
